/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */


export interface SensingSystemArchitectureDesignState {
  /** Data for all studies */
  studies: Study[]

  /** ID of the current study */
  currentStudyId: string | null

  /** Experiments of the current study */
  experiments: Experiment[] | null

  /** ID of the current experiment */
  currentExperimentId: string | null

  /** Sensor ID to name mapping */
  sensorIdToName: { [key: string]: string }

  /** Greenline ID to properties mapping */
  greenlineIdToProperties: { [key: string]: any }

  /** Greenline ID to plot data mapping */
  greenlineIdToPlotData: { [key: string]: any }

  /** Greenline ID to run state mapping */
  greenlineIdToRunState: { [key: string]: any }

  /** Sensor ID to blue line run state mapping */
  sensorIdToBlueLineRunState: { [key: string]: any }

  /** Sensor ID to violations run state mapping */
  sensorIdToViolationsRunState: { [key: string]: any }

  /** Dialog state */
  dialogs: Dialogs

  /** Wizard state */
  wizards: Wizards

  /** Table state */
  table: Table

  loading: LoadingState
}

export interface Study {
  /** Study's ID */
  id: string

  /** Study's ID */
  studyId: string

  /** Database item type */
  type: 'study'

  /** Study's name */
  name: string

  /** Study's description */
  description: string

  /** User ID of study's owner*/
  owner: string

  /** Study's creation date */
  created: string

  /** Study's last modification date */
  updated: string

  /** Study's Plan of Record state */
  planOfRecord: boolean

  /** Study's Archive state */
  archive: boolean

  /** ADAS Program of the study if linked, otherwise null */
  adasProgramId: string | null
}

export interface Experiment {
  /** Experiment's ID */
  id: string

  /** ID of the experiment's linked study */
  studyId: string

  /** Database item type */
  type: 'experiment'

  /** Experiment's name */
  name: string

  /** Experiment's description */
  description: string

  /** User ID of experiment's owner*/
  owner: string

  /** Experiment's creation date */
  created: string

  /** Experiment's last modification date */
  updated: string

  /** Manage data of experiment */
  data: ExperimentData

  /** Manage state of experiment */
  state: ExperimentState
}

export interface ExperimentData {
  /** Redline data set ID of the experiment if linked, otherwise null */
  redlineDataSetId: string | null

  /** Sensor configuration */
  sensors: any

  /** Selected sensors */
  selectedSensors: { [key: string]: true } // Key is sensor position id eg sensor-id_sensor-location

  /** Selected sensors for green line modification */
  selectedSensorsForGreenlineModify: { [key: string]: true } // Key is sensor position id eg sensor-id_sensor-location

  /** Stores sensor position ids in order that they appear in the table */
  sensorTableOrder: string[]

  /** Green line IDs that exist */
  existingGreenLines: any

  /** Plot data */
  plot: any

  /** Green line ID to properties (eg name) */
  greenlineIdToProperties: { [key: string]: any }
}

export interface ExperimentState {
  /** Generate Green Line is loading */
  generateGreenLineLoading: boolean
}

export interface Dialog {
  /** Control whether dialog is open */
  isOpen: boolean
}

export interface StudyDialog extends Dialog {
  /** Study ID when editing or deleting, null when creating */
  studyId: string | null

  /** Type of action */
  action: StudyDialogAction | null
}

export interface DataSetDialog extends Dialog {
  /** Data set ID */
  dataSetId: string | null
}
export enum EViewDataSetErrorsDialogDataType {
  BlueLine = 'blueLine',
  Violations = 'violations',
  GreenLine = 'greenLine'
}
export interface ViewDataSetErrorsDialog extends DataSetDialog {
  /** If the data set is a blue line or violations */
  dataType: EViewDataSetErrorsDialogDataType | null
}

export interface Table {
  /** Study data is fetching */
  isFetching: boolean

  /** Changes to study data is saving */
  isSaving: boolean
}

/** Type of dialog action */
export type StudyDialogAction = 'create' | 'edit' | 'delete'

export interface Dialogs {
  /** Dialog to create and update a study */
  studyForm: StudyDialog

  /** Dialog to delete a study */
  confirmStudyAction: StudyDialog

  /** Dialog to download a data set */
  downloadDataSet: DataSetDialog

  /** Dialog to view data set errors */
  viewDataSetErrors: ViewDataSetErrorsDialog
}

export interface Wizard {
  /** Control whether wizard is open */
  isOpen: boolean

  /** Wizard data */
  data: any | null
}

export interface Object {
  id: string
  name: string
  type: string
  pixelsAcrossShortDimension: number
  comments: string
}

export interface Wizards {
  /** Wizard to connect an ADAS Program to a study */
  connectAdasProgram: Wizard

  /** Wizard to edit a sensor configuration */
  editSensor: Wizard
}

export interface LoadingState {
  /** Fetch experiments loading state */
  fetchExperiments: boolean

  /** Generate green line loading state */
  generateGreenLines: boolean

  /** Commonize green line loading state */
  commonizeGreenLines: boolean

  /** Smooth green line loading state */
  smoothGreenLines: boolean

  /** Symmetrize green lines loading state */
  symmetrizeGreenLines: boolean
}

export interface GreenLineError {
  /** Exception message */
  exception: string

  /** Stack trace log */
  stackTrace: string

  /** Date error occurred */
  timestamp: string

  /** Activity running when error occurred */
  activity: GreenLineActionTypes
}

export enum GreenLineActionTypes {
  /** Generate green line */
  GENERATE = 'GENERATE',

  /** Commonize green line */
  COMMONIZE = 'COMMONIZE',

  /** Smooth green line */
  SMOOTH = 'SMOOTH',

  /** Symmetrize green line */
  SYMMETRIZE = 'SYMMETRIZE',
}
